import { formatDateOnlySafe, formatDateTimeDisplay, safeParseDate } from "./date";
import {
  getRuntimeTenantTimeZone,
  resolveIanaTimeZone,
} from "./studio-clock";

const FALLBACK = "-";

export type StudioInstantPreset = "table" | "short" | "long";

function isUtcMidnightInstant(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

function dateOptionsForPreset(
  preset: StudioInstantPreset,
): Intl.DateTimeFormatOptions {
  switch (preset) {
    case "table":
      return { month: "short", day: "numeric" };
    case "long":
      return {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      };
    case "short":
    default:
      return { month: "short", day: "numeric", year: "numeric" };
  }
}

/**
 * Formats an API instant in the tenant studio time zone for staff-facing UI.
 * Falls back to formatDateTimeDisplay when no valid IANA zone is available.
 */
export function formatStudioInstant(
  value: Date | string | null | undefined,
  preset: StudioInstantPreset = "short",
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  const date = safeParseDate(value);
  if (!date) return FALLBACK;

  const tz = resolveIanaTimeZone(timeZone);
  if (!tz) {
    return formatDateTimeDisplay(value, {
      compact: preset === "table",
      fallback: FALLBACK,
    });
  }

  if (isUtcMidnightInstant(date)) {
    return (
      formatDateOnlySafe(date, dateOptionsForPreset(preset), "en-US") ??
      FALLBACK
    );
  }

  const locale = "en-US";
  const dateLabel = new Intl.DateTimeFormat(locale, {
    ...dateOptionsForPreset(preset),
    timeZone: tz,
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  }).format(date);

  return `${dateLabel} · ${timeLabel}`;
}

/** Appends a short zone name when studio TZ is set, e.g. "Sep 30, 2026 · 3:42 PM PT". */
export function formatStudioInstantWithZone(
  value: Date | string | null | undefined,
  preset: StudioInstantPreset = "short",
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  const base = formatStudioInstant(value, preset, timeZone);
  if (base === FALLBACK) return base;

  const tz = resolveIanaTimeZone(timeZone);
  if (!tz) return base;

  const date = safeParseDate(value);
  if (!date || isUtcMidnightInstant(date)) return base;

  const zonePart = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "shortGeneric",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;

  return zonePart ? `${base} ${zonePart}` : base;
}
